import { useEffect, useRef } from 'react'
import { reset, destroy, formValueSelector, isDirty } from 'redux-form'
import { toast, Slide } from 'react-toastify'
import { openModal } from '../../actions/app'
import { Fade, Button, Error, Loader } from '../../components/common'
import RefundedExamsTable from '../../components/refund/RefundedExamsTable'
import RefundOrgSearchForm from '../forms/RefundOrgSearchForm'
import StudentSearchForm from '../forms/StudentSearchForm'
import SelectExamsForRefundForm from '../forms/SelectExamsForRefundForm'
import { getExamsSearched, getExamsForRefundSubmissionStatus } from '../../selectors/refundExam'
import { resetRefundExamFlow, fetchExamsForStudent } from '../../actions/refundExam'
import {
  ORG_SEARCH_FORM_NAME,
  STUDENT_SEARCH_FORM_NAME,
  ORG_SEARCH_FIELD_ID,
  STUDENT_SEARCH_FIELD_ID,
  EXAM_SEARCH_FORM_NAME,
} from '../../constants/RefundExamConstants'

const orgSearchSelector = formValueSelector(ORG_SEARCH_FORM_NAME)
const studentSearchSelector = formValueSelector(STUDENT_SEARCH_FORM_NAME)

const mapStateToProps = state => {
  const orgId = orgSearchSelector(state, ORG_SEARCH_FIELD_ID)
  const studentId = studentSearchSelector(state, STUDENT_SEARCH_FIELD_ID)
  const exams = getExamsSearched(state)
  const dirty = isDirty(EXAM_SEARCH_FORM_NAME)(state)
  const {
    fetching: submitting,
    fetched: submitted,
    error: submissionError,
  } = getExamsForRefundSubmissionStatus(state)
  return {
    orgId,
    studentId,
    exams,
    submitting,
    submitted,
    submissionError,
    dirty,
  }
}

const RefundExamsPage = ({
  orgId,
  studentId,
  exams,
  submitting,
  submitted,
  submissionError,
  dirty,
  resetRefundExamFlow,
  fetchExamsForStudent,
  openModal,
  destroy,
  reset,
}) => {
  const refundButton = useRef(null)
  useEffect(() => {
    if (studentId) {
      destroy(EXAM_SEARCH_FORM_NAME)
      fetchExamsForStudent(orgId, studentId)
    }
    if (submitted) {
      toast['success']('The selected exam(s) have been refunded.', {
        position: toast.POSITION.BOTTOM_LEFT,
        autoClose: 10000,
        transition: Slide,
      })
      resetRefundExamFlow()
      reset(ORG_SEARCH_FORM_NAME)
      reset(STUDENT_SEARCH_FORM_NAME)
      reset(EXAM_SEARCH_FORM_NAME)
    }
  }, [studentId, submitted])

  return (
    <Fade>
      <div className="container">
        <div className="row">
          <div className="col-xs-12">
            <h1>Refund Exams</h1>
          </div>
          <div className="col-xs-12">
            <div className="row">
              <div className="col-xs-6">
                <h2>Step 1 of 3: Select School</h2>
                <RefundOrgSearchForm />
              </div>
            </div>
            <hr />
            <div className="row">
              <div className="col-xs-6">
                <h2>Step 2 of 3: Select Student</h2>
                {orgId && <StudentSearchForm orgId={orgId} />}
              </div>
            </div>
            <hr />
            <div className="row">
              <div className="col-xs-6">
                <h2>Step 3 of 3: Select Exam(s)</h2>
                {studentId && <SelectExamsForRefundForm exams={exams} />}
              </div>
            </div>
            <div className="row">
              <div className="col-xs-6" style={{ marginTop: '20px' }}>
                {submissionError && <Error message={submissionError} />}
                <Button
                  isDisabled={!orgId && !studentId}
                  onClick={async () => {
                    await resetRefundExamFlow()
                    reset(ORG_SEARCH_FORM_NAME)
                    reset(STUDENT_SEARCH_FORM_NAME)
                    reset(EXAM_SEARCH_FORM_NAME)
                  }}
                  style={{ marginRight: '20px' }}
                >
                  Cancel
                </Button>
                <button
                  type="submit"
                  className="btn btn-sm btn-primary"
                  ref={refundButton}
                  disabled={!dirty || submitting}
                  style={{ position: 'relative' }}
                  onClick={() =>
                    openModal('ConfirmRefundExamModal', {
                      modalCloseFocusElem: refundButton.current,
                    })
                  }
                >
                  Remove Fee
                  {submitting && (
                    <Loader
                      size="sm"
                      style={{ position: 'absolute', top: '12px', right: '10px' }}
                    />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
        <RefundedExamsTable />
      </div>
    </Fade>
  )
}

export default connect(mapStateToProps, {
  resetRefundExamFlow,
  fetchExamsForStudent,
  reset,
  destroy,
  openModal,
})(RefundExamsPage)
